// for dashboard related operations

import type { RuleAction, RuleCondition } from '../RuleRecommendationsService';

import HttpServiceSingleton from '../HttpService/HttpService';

const API_BASE = process.env.REACT_APP_RULES_API ?? '';

/**
 * Interface for rule list request filters
 */
export interface RuleFilters {
  status?: number[];
  type?: number[];
  needUpdate?: boolean[];
  name?: string[];
  description?: string[];
  duplicateOf?: string;
  entitlementValueName?: string[];
  endpointName?: string[];
  entitlementType?: string[];
  userMatchCount?: number[];
  yearlyRequestVolume?: number[];
  confidence?: number[];
  impact?: string[];
  objectName?: string[];
  objectValue?: string[];
  search?: string; // Search remains a single string
}

export interface RulePagination {
  max?: number;
  page?: number;
}

/**
 * Interface for rule list request body
 */
export interface RuleListRequestBody {
  filters?: RuleFilters;
  pagination?: RulePagination;
}

/**
 * Interface for a single rule (extending the existing structure with new fields)
 */
export interface Rule {
  recommendedObjectKey: number;
  name: string;
  description: string;
  userMatchCount: number;
  impact: string;
  yearlyRequestVolume: number;
  // TODO: REVERT - Remove when backend uses yearlyRequestVolume
  monthlyRequestVolume?: number;
  entitlements: number;
  confidence: number;
  conditions: RuleCondition[];
  actions: RuleAction[];
  isNew: boolean;

  totalCount?: number; // Temporary: backend placed this at rule level instead of response level
}

/**
 * Interface for rule list response
 */
export interface RuleListResponse {
  data: Rule[];
  pagination: {
    nextPage: number | null;
    currentPage: number;
  };
  userAccess?: 'edit' | 'view' | 'none';
  totalNewObjects?: number;
  totalCount?: number; // Total count of rules (will replace rule-level totalCount)
}

export interface ActivateRuleRequestBody {
  activatedBy: number;
  comments?: string;
  name: string;
  description: string;
  actions: {
    entitlementValueName: string;
    entitlementTypeName: string;
    endpointName: string;
  }[];
}

export interface ActivateRuleResponse {
  success: boolean;
  message: string;
  recommendedObjectKey: number;
  activatedBy: number;
  username: string | null;
  comments: string | null;
  ruleId: number;
  data: {
    success: boolean;
    message: string;
    ruleId: number;
    username: string | null;
    ruleServiceUrl: string;
    ruleCreationResponse: {
      msg: string;
    };
    removedEntitlements: unknown[];
  };
}

export interface RejectRuleRequestBody {
  rejectedBy: number;
  reason?: string;
}

export interface RejectRuleResponse {
  success: boolean;
  message: string;
  recommendedObjectKey: number;
  rejectedBy: number;
  comments: string;
  data: {
    success: boolean;
    message: string;
  };
}

/**
 * Service for Rules API operations
 */
const RulesService = {
  /**
   * Fetches rules list with filters and pagination
   * @param requestBody - Request body with filters and pagination
   * @returns Promise with rules list response
   */
  getRulesList(
    requestBody: RuleListRequestBody = {}
  ): Promise<RuleListResponse> {
    const url = `${API_BASE}/ECMv6/api/intelligence/ruleRecommendations/rules/`;

    return HttpServiceSingleton.post(url, requestBody, undefined, true, {
      shouldThrowOnError: true,
    }) as Promise<RuleListResponse>;
  },

  /**
   * Fetches detailed information for a specific rule
   * @param recommendedObjectKey - Unique identifier for the rule
   * @returns Promise with rule details
   */
  getRuleDetails(recommendedObjectKey: number): Promise<Rule> {
    const url = `${API_BASE}/ECMv6/api/intelligence/ruleRecommendations/rules/${recommendedObjectKey}`;

    return HttpServiceSingleton.get(url) as Promise<Rule>;
  },

  /**
   * Activates a rule with the provided configuration
   * @param recommendedObjectKey - Unique identifier for the rule
   * @param requestBody - Request body with activation details
   * @returns Promise with activation response
   */
  activateRule(
    recommendedObjectKey: string,
    requestBody: ActivateRuleRequestBody
  ): Promise<ActivateRuleResponse> {
    const url = `${API_BASE}/ECMv6/api/intelligence/ruleRecommendations/rules/${recommendedObjectKey}/activate`;

    return HttpServiceSingleton.post(
      url,
      requestBody,
      undefined, // errorCallback
      true, // shouldStringifyBody
      { shouldThrowOnError: true } // for error handling
    ) as Promise<ActivateRuleResponse>;
  },

  /**
   * Rejects a rule with the provided reason
   * @param recommendedObjectKey - Unique identifier for the rule
   * @param requestBody - Request body with rejection details
   * @returns Promise with rejection response
   */
  rejectRule(
    recommendedObjectKey: number,
    requestBody: RejectRuleRequestBody
  ): Promise<RejectRuleResponse> {
    const url = `${API_BASE}/ECMv6/api/intelligence/ruleRecommendations/rules/${recommendedObjectKey}/reject`;

    return HttpServiceSingleton.post(
      url,
      requestBody,
      undefined, // errorCallback
      true, // shouldStringifyBody
      { shouldThrowOnError: true } // for error handling
    ) as Promise<RejectRuleResponse>;
  },
};

export default RulesService;
